import {
  invokeQCForAsset,
  updateCampaignMediaDeliveryPersonContacts,
  waitForAssetQCStatus,
  invokeCampaignStatusUpdate,
} from "services/apiMiddleware";
import { useEffect, useMemo, useState, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Redirect } from "react-router-dom";
import { Button, Message, Loader, Icon, Divider, Progress, Accordion } from "semantic-ui-react";
import { useTranslation } from "react-i18next";
import { toast } from "components/Toast/Toast";
import _ from "lodash";
import { AssetStatus } from "models";
import { getAssets } from "store/selectors";
import { getFileNameFromAssetMedia } from "services/common";

const Msg = ({ success, error, warning, children }) => {
  return (
    <h3 className="result-message">
      {children} {success ? <Icon color="green" name="check" /> : null}
      {warning ? <Icon color="yellow" name="warning" /> : null}
      {error ? <Icon color="red" name="x" /> : null}
    </h3>
  );
};

const splitActions = (actions) => {
  const assetCreateActions = actions.filter((act) => act.type === "create");
  const assetUpdateActions = actions.filter(
    (act) => act.type === "update" && !act.removeFromCampaign
  );
  const assetRemoveFromCampaignActions = actions.filter(
    (act) => act.type === "update" && act.removeFromCampaign
  );
  const manualQCAssets = actions.filter((act) => act.type === "runQC");
  const campaignActions = actions.filter((act) => act.type === "update-campaign");
  return {
    assetCreateActions,
    assetUpdateActions,
    assetRemoveFromCampaignActions,
    manualQCAssets,
    campaignActions,
  };
};

const AssetUploadProgress = ({ uploadProgressArr, t }) => {
  const [showDetails, setShowDetails] = useState(false);

  const overallProgress = useMemo(() => {
    const { size, loaded } = _.reduce(
      uploadProgressArr,
      ({ size, loaded }, progress) => ({
        size: size + progress.size,
        loaded: loaded + progress.loaded,
      }),
      { size: 0, loaded: 0 }
    );
    return Math.round((loaded / size) * 100);
  }, [uploadProgressArr]);

  return (
    <>
      <Progress
        className={overallProgress < 100 ? "bmo-purple-progress" : undefined}
        percent={overallProgress}
        progress
        success={overallProgress === 100}
      >
        {t("delivery_upload_overall_upload_progress")}
      </Progress>
      <div>
        <Accordion fluid styled>
          <Accordion.Title active={showDetails} onClick={() => setShowDetails(!showDetails)}>
            <span>
              <Icon name="dropdown" />
            </span>
            {t("delivery_upload_show_upload_details")}
          </Accordion.Title>
          <Accordion.Content active={showDetails}>
            <div>
              {uploadProgressArr.map((uploadProgress, i) => {
                const percent = Math.round((uploadProgress.loaded / uploadProgress.size) * 100);
                return (
                  <div key={uploadProgress.id}>
                    {i > 0 && <Divider />}
                    <Progress
                      className={percent < 100 ? "bmo-purple-progress" : undefined}
                      percent={percent}
                      progress
                      success={percent === 100}
                    >
                      <div>
                        {t("delivery_upload_upload_progress_message")} <i>{uploadProgress.name}</i>
                      </div>
                    </Progress>
                  </div>
                );
              })}
            </div>
          </Accordion.Content>
        </Accordion>
      </div>
    </>
  );
};

const AssetActionsSuccessMsg = ({ actionsArr, translationKey, t }) => {
  if (actionsArr.length === 0) return null;
  return <Msg success>{t(translationKey, { assetsLen: actionsArr.length })}</Msg>;
};

const DeliveryUpload = (props) => {
  const { actions, campaign, isAdmin } = props;
  const [error, setError] = useState(null);
  const [uploadOK, setUploadOK] = useState(false);
  const [updateOK, setUpdateOK] = useState(false);
  const [removeOK, setRemoveOK] = useState(false);
  const [result, setResult] = useState();
  const [currentOperationMsg, setCurrentOperationMsg] = useState(null);
  const [qcCheckedAssetsMsgs, setQcCheckedAssetsMsgs] = useState([]);
  const [qcSuccessCount, setSuccessCount] = useState(0);
  const [qcFailedCount, setFailedCount] = useState(0);
  const [qcWarningCount, setWarningCount] = useState(0);
  const dispatch = useDispatch();
  const { t } = useTranslation("common");

  const {
    assetCreateActions,
    assetUpdateActions,
    assetRemoveFromCampaignActions,
    manualQCAssets,
    campaignActions,
  } = useMemo(() => splitActions(actions), [actions]);

  const { uploadProgressArr } = useSelector(getAssets);

  const setCurrentOperationMsgWrapper = (actionsArr, translationKey) => {
    if (actionsArr.length === 0) return;
    setCurrentOperationMsg(<Msg>{t(translationKey, { assetsLen: actionsArr.length })}</Msg>);
  };

  const waitForAssetQCResult = async (asset, isManualQCAsset) => {
    let resultMsg;
    let updatedAsset;

    setCurrentOperationMsg(
      <Msg>
        {t("delivery_upload_checking_asset_status")} <i>{asset.name}</i>
      </Msg>
    );

    try {
      updatedAsset = isManualQCAsset
        ? (await invokeQCForAsset(asset)).data // Run QC manually for an existing asset
        : (await waitForAssetQCStatus(asset.id)).data; // Check status for a newly created asset
      const status = updatedAsset.status;
      const deliveryUploadAssetCheckedMessage = t("delivery_upload_asset_checked_message");
      resultMsg =
        status === AssetStatus.QC_FAILED ? (
          <Msg error key={_.uniqueId("fail_")}>
            <i>{asset.name}</i>: {deliveryUploadAssetCheckedMessage}
          </Msg>
        ) : status === AssetStatus.QC_CAUTION ? (
          <Msg warning key={_.uniqueId("warning_")}>
            <i>{asset.name}</i>: {deliveryUploadAssetCheckedMessage}
          </Msg>
        ) : (
          <Msg success key={_.uniqueId("success_")}>
            <i>{asset.name}</i>: {deliveryUploadAssetCheckedMessage}
          </Msg>
        );
    } catch (error) {
      resultMsg = (
        <Msg error key={_.uniqueId("fail_")}>
          <i>{asset.name}:</i> {error.message}
        </Msg>
      );
      updatedAsset = {};
    }
    setQcCheckedAssetsMsgs((msgs) => msgs.concat(resultMsg));

    return updatedAsset;
  };

  const waitForMultiPagePDFAssetResults = async (
    individualAssetIdsForMultiPagePDF,
    originalAsset
  ) => {
    // If asset was the original asset of the multi-page PDF, wait for the individual assets' QC's to finish
    if (individualAssetIdsForMultiPagePDF) {
      for (let j = 0; j < individualAssetIdsForMultiPagePDF.length; j++) {
        // We need the asset's name for UI but it doesn't need to be fetched from GraphQL:
        // Name = "<original asset's file name without file extension>-<index>.pdf"
        const fileName = getFileNameFromAssetMedia(originalAsset.media);
        const individualAssetName = _.replace(fileName, ".pdf", `-${j}.pdf`);
        const individualAsset = {
          id: individualAssetIdsForMultiPagePDF[j],
          name: individualAssetName,
        };
        await waitForAssetQCResult(individualAsset, false);
      }
    }
  };

  const continueResolverRef = useRef(null);

  const waitForContinue = () =>
    new Promise((resolve) => {
      continueResolverRef.current = resolve;
    });

  const handleContinue = () => {
    if (continueResolverRef.current) {
      continueResolverRef.current();
      continueResolverRef.current = null;
    }
  };

  useEffect(() => {
    (async function () {
      try {
        // Create new assets
        setCurrentOperationMsgWrapper(assetCreateActions, "delivery_upload_sending_assets_message");
        const newAssets = await Promise.all(
          assetCreateActions.map((act) =>
            dispatch.assets.create({ createProps: act.props, media: act.media })
          )
        );
        setUploadOK(true);

        // Update assets
        setCurrentOperationMsgWrapper(
          assetUpdateActions,
          "delivery_upload_updating_assets_message"
        );
        await Promise.all(
          assetUpdateActions.map((act) => dispatch.assets.update({ updateProps: act.props }))
        );
        setUpdateOK(true);

        // Remove assets from the campaign
        setCurrentOperationMsgWrapper(
          assetRemoveFromCampaignActions,
          "delivery_upload_removing_assets_from_campaign_message"
        );
        await Promise.all(
          assetRemoveFromCampaignActions.map((act) =>
            dispatch.assets.update({ updateProps: act.props })
          )
        );
        setRemoveOK(true);

        // Update campaign
        await Promise.all(
          campaignActions.map((act) => dispatch.campaigns.update({ updateProps: act.props }))
        );

        const allAssets = _.concat(newAssets, manualQCAssets);
        for (let i = 0; i < allAssets.length; i++) {
          const isManualQCAsset = !!allAssets[i].props;
          const asset = isManualQCAsset ? allAssets[i].props : allAssets[i];

          const updatedAsset = await waitForAssetQCResult(asset, isManualQCAsset);

          await waitForMultiPagePDFAssetResults(
            updatedAsset.individualAssetIdsForMultiPagePDF,
            updatedAsset
          );

          if (updatedAsset.status === "QC_CAUTION") setWarningCount((prev) => prev + 1);
          else if (updatedAsset.status === "QC_FAILED") setFailedCount((prev) => prev + 1);
          else setSuccessCount((prev) => prev + 1);
        }

        // Add contributor's email to campaign's media delivery person contacts list if new assets were uploaded
        if (!isAdmin && assetCreateActions.length > 0) {
          const campaignID = assetCreateActions[0].props.campaignID;
          const result = await updateCampaignMediaDeliveryPersonContacts(campaignID);
          if (result.errorMessage) throw new Error(result.errorMessage);
        }

        // When asset is removed from campaign, campaing status is updated
        if (assetRemoveFromCampaignActions.length > 0) {
          const result = await invokeCampaignStatusUpdate(campaign.id);
          if (result.errorMessage) throw new Error(result.errorMessage);
        }

        if (allAssets.length > 0) {
          toast(
            undefined,
            t("delivery_upload_assets_checked_message", { assetsLen: allAssets.length })
          );
        }
        setResult("success");
        await waitForContinue();
        setResult("postSuccess");
      } catch (err) {
        setError(err.message);
      }
      dispatch.assets.uploadProgressSuccess(); // Empty upload progress object array in state
    })();
  }, []);

  if (error) return <Message error>{error}</Message>;

  return (
    <div>
      <div className="result">
        {!result ? (
          <div className="delivery-upload-loader-container">
            <span>{t("delivery_upload_loading")}</span>
            <Loader active size="massive" indeterminate inline={"centered"} />
            {currentOperationMsg}
            {!uploadOK && <AssetUploadProgress uploadProgressArr={uploadProgressArr} t={t} />}
          </div>
        ) : result === "success" || result === "postSuccess" ? (
          <Icon color="green" name="check" size="massive" />
        ) : (
          <Icon color="red" name="x" size="massive" />
        )}
      </div>
      <Divider />
      <>
        {uploadOK && (
          <AssetActionsSuccessMsg
            actionsArr={assetCreateActions}
            translationKey={"delivery_upload_sending_assets_success_message"}
            t={t}
          />
        )}
        {updateOK && (
          <AssetActionsSuccessMsg
            actionsArr={assetUpdateActions}
            translationKey={"delivery_upload_updating_assets_success_message"}
            t={t}
          />
        )}
        {removeOK && (
          <AssetActionsSuccessMsg
            actionsArr={assetRemoveFromCampaignActions}
            translationKey={"delivery_upload_removing_assets_from_campaign_success_message"}
            t={t}
          />
        )}
        {qcCheckedAssetsMsgs}
        {qcSuccessCount + qcWarningCount + qcFailedCount > 0 && (
          <>
            <Divider />
            <div>
              <Msg>
                {t("delivery_upload_assets_checked_results_header", {
                  assetsLen: qcSuccessCount + qcWarningCount + qcFailedCount,
                })}
              </Msg>
              {qcSuccessCount > 0 && (
                <Msg success>
                  {t("delivery_upload_assets_checked_results_succes")} {qcSuccessCount}
                </Msg>
              )}
              {qcWarningCount > 0 && (
                <Msg warning>
                  {t("delivery_upload_assets_checked_results_warning")} {qcWarningCount}
                </Msg>
              )}
              {qcFailedCount > 0 && (
                <Msg error>
                  {t("delivery_upload_assets_checked_results_failed")} {qcFailedCount}
                </Msg>
              )}
            </div>
            <Divider />
          </>
        )}
        <div>
          {result === "success" && (
            <Button
              fluid
              size="large"
              style={{
                color: "white",
                backgroundColor: "#4700a3",
              }}
              onClick={handleContinue}
            >
              {t("continue")}
            </Button>
          )}
        </div>
      </>
      {result === "postSuccess" && (
        <Redirect to={(isAdmin ? "/campaigns/" : "/deliveries/") + campaign.id} />
      )}
    </div>
  );
};

export default DeliveryUpload;
